import { useState, useEffect, useRef, useCallback } from "react";

// Define the command mapping type
type CommandMapping = Record<
  string,
  {
    phrases: string[];
    action: () => void;
    description: string;
  }
>;

export function useVoiceRecognition(
  commandMapping: CommandMapping,
  enabled: boolean
) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [recentTranscripts, setRecentTranscripts] = useState<string[]>([]);
  const [status, setStatus] = useState("inactive");
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Use refs to prevent infinite re-renders
  const commandMappingRef = useRef(commandMapping);
  const enabledRef = useRef(enabled);

  // Update refs when props change
  useEffect(() => {
    commandMappingRef.current = commandMapping;
  }, [commandMapping]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Recognition instance ref
  const recognitionRef = useRef<any>(null);

  // Initialize speech recognition
  useEffect(() => {
    // Check if speech recognition is available
    if (
      !("webkitSpeechRecognition" in window) &&
      !("SpeechRecognition" in window)
    ) {
      console.error("Speech recognition not supported in this browser");
      setStatus("unsupported");
      return;
    }

    // Create recognition instance
    const SpeechRecognition =
      window.webkitSpeechRecognition || window.SpeechRecognition;
    recognitionRef.current = new SpeechRecognition();

    // Configure recognition
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = "en-US";

    // Set up event handlers
    recognitionRef.current.onstart = () => {
      setStatus("listening");
      setIsListening(true);
    };

    recognitionRef.current.onend = () => {
      if (enabledRef.current && isListening) {
        // Restart if still enabled
        try {
          recognitionRef.current.start();
        } catch (error) {
          console.error("Error restarting recognition:", error);
          setStatus("error");
          setIsListening(false);
        }
      } else {
        setStatus("inactive");
        setIsListening(false);
      }
    };

    recognitionRef.current.onerror = (event: any) => {
      console.error("Recognition error:", event.error);

      if (event.error === "not-allowed") {
        setPermissionDenied(true);
        setStatus("permission-denied");
        setIsListening(false);
      } else {
        setStatus("error");
      }
    };

    recognitionRef.current.onresult = (event: any) => {
      const current = event.resultIndex;
      const result = event.results[current];
      const transcriptText = result[0].transcript.trim().toLowerCase();

      setTranscript(transcriptText);

      // Only process final results
      if (result.isFinal) {
        // Add to recent transcripts
        setRecentTranscripts((prev) => [transcriptText, ...prev].slice(0, 5));

        // Check for commands
        Object.values(commandMappingRef.current).forEach((command) => {
          if (
            command.phrases.some((phrase) => transcriptText.includes(phrase))
          ) {
            command.action();
          }
        });

        // Clear current transcript
        setTranscript("");
      }
    };

    // Start recognition if enabled
    if (enabledRef.current) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error("Error starting recognition:", error);
      }
    }

    // Cleanup
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error("Error stopping recognition:", error);
        }
      }
    };
  }, []); // Empty dependency array - only run once

  // Handle enabled state changes
  useEffect(() => {
    if (!recognitionRef.current) return;

    if (enabled && !isListening && !permissionDenied) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error("Error starting recognition:", error);
      }
    } else if (!enabled && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }
  }, [enabled, isListening, permissionDenied]);

  // Toggle listening function
  const toggleListening = useCallback(() => {
    if (permissionDenied) return;

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    } else {
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error("Error starting recognition:", error);
      }
    }
  }, [isListening, permissionDenied]);

  return {
    isListening,
    transcript,
    recentTranscripts,
    status,
    toggleListening,
    permissionDenied,
  };
}
